// User-to-user connections, a mutual relationship that's independent of the
// Top 8 (like a friend request). One document per pair, keyed by the two uids
// sorted so either side resolves to the same id.
import { db, functions } from './firebase';
import {
  doc, getDoc, updateDoc, deleteDoc, addDoc, collection, query, where, getDocs,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export function connId(a, b) {
  return [a, b].sort().join('__');
}

// All of a user's connections as a map: otherUid -> status ('pending'|'accepted').
// Pass { acceptedOnly: true } when reading SOMEONE ELSE's connections: pending
// docs are no longer world-readable, so an unfiltered query for another user
// would hit docs you can't read and fail entirely. Filtering to accepted keeps
// the query within what the rules allow. Querying your own uid is always fine
// (you're a participant in every returned doc).
export async function getConnections(uid, { acceptedOnly = false } = {}) {
  if (!uid) return {};
  try {
    const base = [collection(db, 'connections'), where('users', 'array-contains', uid)];
    const q = acceptedOnly
      ? query(...base, where('status', '==', 'accepted'))
      : query(...base);
    const snap = await getDocs(q);
    const map = {};
    snap.forEach(d => {
      const c = d.data();
      const other = (c.users || []).find(u => u !== uid);
      if (other) map[other] = c.status;
    });
    return map;
  } catch {
    return {};
  }
}

// All of a user's connections with status + requestedBy, as
// [{ uid: otherUid, status, requestedBy }]. One array-contains query (no composite
// index), so callers can derive accepted vs. outgoing-pending client-side.
export async function getConnectionsFull(myUid) {
  if (!myUid) return [];
  try {
    const snap = await getDocs(query(collection(db, 'connections'), where('users', 'array-contains', myUid)));
    const out = [];
    snap.forEach(d => {
      const c = d.data();
      const other = (c.users || []).find(u => u !== myUid);
      if (other) out.push({ uid: other, status: c.status, requestedBy: c.requestedBy });
    });
    return out;
  } catch {
    return [];
  }
}

// Returns { id, users, requestedBy, status } or null if no connection exists.
export async function getConnection(myUid, otherUid) {
  if (!myUid || !otherUid) return null;
  try {
    const snap = await getDoc(doc(db, 'connections', connId(myUid, otherUid)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch {
    return null;
  }
}

// Send a connection request. Goes through the requestConnection Cloud Function
// (rate-limited, server-creates the pending doc + notification) so requests can't
// be spammed. `me` is kept for call-site compatibility; identity comes from auth.
export async function sendConnect(me, other, tileId = null) {
  const fn = httpsCallable(functions, 'requestConnection');
  await fn({ toUid: other.uid, tileId });
}

// The recipient accepts the requester's pending connection.
export async function acceptConnect(myUid, otherUid, me) {
  await updateDoc(doc(db, 'connections', connId(myUid, otherUid)), {
    status: 'accepted',
    updatedAt: new Date(),
  });
  await addDoc(collection(db, 'users', otherUid, 'notifications'), {
    type: 'connect_accepted',
    fromUid: myUid,
    fromUsername: me.username || '',
    fromDisplayName: me.displayName || '',
    fromPhotoURL: me.photoURL || null,
    body: `${me.displayName || 'Someone'} accepted your connection.`,
    read: false,
    createdAt: new Date(),
  });
}

// Cancel a pending request, decline one, or disconnect an accepted connection.
export async function removeConnect(myUid, otherUid) {
  await deleteDoc(doc(db, 'connections', connId(myUid, otherUid)));
}

// Block / unblock a user. Goes through setBlock (also severs any connection).
export async function setBlock(uid, blocked) {
  return httpsCallable(functions, 'setBlock')({ uid, blocked });
}

// The set of uids the signed-in user has blocked (for filtering lists / profiles).
export async function getMyBlocks(myUid) {
  if (!myUid) return new Set();
  try {
    const snap = await getDocs(collection(db, 'users', myUid, 'blocks'));
    return new Set(snap.docs.map(d => d.id));
  } catch {
    return new Set();
  }
}
